import { axiosInstance, axiosInstanceV1 } from "./axiosInstance";
import { BloqueEncuesta, DatosEncuesta, DatosQuiz, QuizCompleto } from "./lmsQuiz.service";

/*
	Portal de Clase (LMS, Parte A, 2026-10-17). Dos caras de la misma clase:
	 - personal:  /admin/classPortal/{horario}/_...   (v2; el servidor decide si la clase es suya o de su plantel)
	 - alumno:    /scholar/_class/{horario}/_...      (v1; el alumno sale de la sesión, nunca se manda su id)
	Las dos devuelven las mismas formas, así que las pantallas comparten componentes a través de PortalApi.
	Archivos: privados; se suben directo con una URL firmada (PUT) y se leen con un enlace firmado de 10 min.
*/

export interface AutorPortal { id: number; name: string; initials: string; photo: string | null; isTeacher: boolean }

export interface ArchivoPublicacion
{
	id: number
	kind: "archivo" | "enlace"
	name: string
	mime: string | null
	size: number
	/** Solo los enlaces traen su dirección; los archivos se piden firmados al abrirlos. */
	url: string | null
}

export interface ComentarioPortal
{
	id: number
	body: string
	whenLabel: string
	dateLabel: string
	author: AutorPortal
	canDelete?: boolean
}

export interface PublicacionPortal
{
	id: number
	type: "anuncio" | "material" | "tarea" | "pendiente" | "encuesta"
	title: string | null
	body: string | null
	pinned: boolean
	status: "publicada" | "programada" | "borrador"
	unit: { id: number; name: string | null } | null
	whenMs: number
	whenLabel: string
	/** CDMX wall time ("2026-10-22T10:00") as the server reads it back; used to fill the edit field. */
	whenInput?: string
	author: AutorPortal
	files: ArchivoPublicacion[]
	commentsCount: number
	lastComments: ComentarioPortal[]
	canEdit: boolean
	/** Solo personal: cuántos alumnos la vieron. */
	views?: { seen: number; total: number }
	/** Solo alumno: aún no la había visto. */
	isNew?: boolean
	/** Viene del curso o del módulo (no se edita ni se borra aquí: se cambia u oculta desde Contenido). */
	inherited?: boolean
	/** Solo personal: de dónde viene ("Del Curso", "Del Módulo", "De Este Horario"), si se cambió en este horario y si el curso ya la quitó. */
	origin?: "curso" | "modulo" | "horario"
	originLabel?: string
	changedHere?: boolean
	retired?: boolean
	templateItemId?: number
	/** Tareas: la ficha (por ahora solo se muestra; las entregas llegan en la Parte B). */
	assignment?: FichaTarea
	/** Pendientes: el alumno ve si lo marcó; el personal ve cuántos lo hicieron. */
	todo?: { done: boolean | number; total?: number }
	/** Encuestas (Parte D): opciones, ajustes y resultados según quién la ve. */
	poll?: BloqueEncuesta | null
}

export interface FichaTarea
{
	points: number
	category: string | null
	categoryOk: boolean
	delivery: string
	deliveryLabel: string
	dueMs: number | null
	dueLabel: string | null
	acceptLate: boolean
	latePenaltyPct: number
	rubric: { name: string; points: number }[]
	audience?: "clase" | "lista"
	/** Solo alumno: cómo va con esta tarea. La nota solo si ya se la devolvieron. */
	mine?: { state: string; late: boolean; score: number | null; scoreHidden: boolean; pointsMax: number } | null
}

export interface UnidadPortal { id: number; name: string; startsOn: string | null; endsOn: string | null; origen: string; /** Comes from the course/module: not edited from the portal. */ inherited?: boolean }

export interface AjustesPortal
{
	coverColor: string
	coverPattern: "circ" | "lin" | "liso"
	coverPhotoUrl: string | null
	hasCoverPhoto: boolean
	welcome: string | null
	wallMode: "maestro" | "comentan" | "publican"
	showMaterials: boolean
	showClassmates: boolean
	showGradeOnReturn: boolean
	showGroupAverage: boolean
}

export interface ProximaClase
{
	date: string
	isToday: boolean
	dayShort: string
	dayNumber: number
	label: string
	startLabel: string
	endLabel: string
	range: string
	modality: "virtual" | "presencial"
	classroom: string | null
	campus: string | null
	opensAtMs: number
	opensLabel: string
	startsAtMs: number
	closesAtMs: number
}

export interface EsquemaDePesos
{
	source: "horario" | "modulo" | "curso" | "sistema"
	sourceLabel: string
	schemeId: number | null
	categories: { name: string; weight: number; esAsistencia: boolean }[]
	total: number
}

export interface ResumenClase
{
	scheduleId: number
	course: string
	level: string
	/** Period name ("Septiembre 2026"), shown under the course. */
	period?: string | null
	title: string
	mentor: string | null
	mentorInitials: string
	scheduleLabel: string
	students: number
	modality: "virtual" | "presencial"
	hasAula: boolean
	campus: string | null
	classroom: string | null
	unit: { index: number; total: number; name: string } | null
	nextClass: ProximaClase | null
	settings: AjustesPortal
	// personal
	role?: "maestro" | "comaestro"
	scheme?: EsquemaDePesos
	canEditScheme?: boolean
	/** Solo personal (Parte B): tarjetas Por Calificar y Necesitan Atención del muro. */
	grading?: TarjetasDelMuro["grading"]
	attention?: TarjetasDelMuro["attention"]
	// alumno
	blocksAula?: boolean
	passLabel?: string | null
	serverNowMs?: number
	newCount?: number
}

export interface ClaseDelPersonal
{
	scheduleId: number
	title: string
	course: string
	level?: string
	period?: string | null
	mentor: string | null
	isMine: boolean
	campus: string | null
	modality: "virtual" | "presencial"
	scheduleLabel: string
	students: number
}

export interface Muro { posts: PublicacionPortal[]; hasMore: boolean; page: number }
export interface Materiales { visible: boolean; units: UnidadPortal[]; posts: PublicacionPortal[] }
export interface AlumnoLista
{
	id?: number
	name: string
	initials: string
	photo: string | null
	me?: boolean
	/** Solo personal (Parte C): promedio del libro de notas (null = aún nada cuenta) y asistencia. */
	average?: number | null
	low?: boolean
	attendance?: { present: number; total: number; pct: number | null }
}
export interface BoletoDeSubida { key: string; url: string; method: "PUT"; contentType: string }
export interface ArchivoNuevo { key?: string; link?: string; id?: number; name: string; mime?: string | null; size?: number }
export interface DatosPublicacion
{
	type?: "anuncio" | "material" | "pendiente" | "encuesta"
	title?: string
	body?: string
	pinned?: boolean
	unitId?: number | null
	publishAt?: string | null
	draft?: boolean
	files?: ArchivoNuevo[]
	/** Solo encuestas. */
	poll?: DatosEncuesta
}

/** Lo que comparten las pantallas de personal y de alumno. */
export interface PortalApi
{
	overview(): Promise<ResumenClase>
	wall(page: number): Promise<Muro>
	materials(limit?: number): Promise<Materiales>
	comments(postId: number): Promise<ComentarioPortal[]>
	addComment(postId: number, body: string): Promise<ComentarioPortal[]>
	deleteComment(postId: number, commentId: number): Promise<ComentarioPortal[]>
	createPost(d: DatosPublicacion): Promise<PublicacionPortal>
	updatePost(postId: number, d: DatosPublicacion): Promise<PublicacionPortal>
	deletePost(postId: number): Promise<void>
	fileUrl(fileId: number): Promise<{ kind: "archivo" | "enlace"; url: string; name: string; mime: string | null }>
}

export interface PortalApiPersonal extends PortalApi
{
	students(): Promise<AlumnoLista[]>
	/** Pestaña Alumnos (Parte C): la lista con promedios y la nota mínima del servidor. */
	roster(): Promise<{ minGrade: number; students: AlumnoLista[] }>
	pin(postId: number, pinned: boolean): Promise<PublicacionPortal>
	units(): Promise<UnidadPortal[]>
	createUnit(d: { name: string; startsOn?: string | null; endsOn?: string | null }): Promise<UnidadPortal[]>
	updateUnit(id: number, d: { name?: string; startsOn?: string | null; endsOn?: string | null }): Promise<UnidadPortal[]>
	deleteUnit(id: number): Promise<UnidadPortal[]>
	settings(): Promise<{ settings: AjustesPortal; scheme: EsquemaDePesos; canEditScheme: boolean }>
	saveSettings(d: Partial<Omit<AjustesPortal, "coverPhotoUrl" | "hasCoverPhoto">> & { coverPhotoKey?: string | null }): Promise<AjustesPortal>
	uploadUrl(d: { name: string; size: number; mime: string; purpose: "adjunto" | "portada" }): Promise<BoletoDeSubida>
}

export interface PortalApiAlumno extends PortalApi
{
	classmates(): Promise<{ visible: boolean; students: AlumnoLista[] }>
	checkTodo(postId: number, done: boolean): Promise<PublicacionPortal>
	/** Encuestas: votar, cambiar el voto o quitarlo ([]). */
	vote(postId: number, optionIds: number[]): Promise<PublicacionPortal>
}

export const apiPersonal = (h: number): PortalApiPersonal =>
{
	const b = "/admin/classPortal/" + h
	return {
		overview: async () => (await axiosInstance.get(b + "/_overview")).data,
		wall: async (page) => (await axiosInstance.get(b + "/_wall", { params: { page } })).data,
		materials: async () => (await axiosInstance.get(b + "/_materials")).data,
		comments: async (p) => (await axiosInstance.get(b + "/_posts/" + p + "/_comments")).data.comments,
		addComment: async (p, body) => (await axiosInstance.post(b + "/_posts/" + p + "/_comments", { body })).data.comments,
		deleteComment: async (p, c) => (await axiosInstance.delete(b + "/_posts/" + p + "/_comments/" + c)).data.comments,
		createPost: async (d) => (await axiosInstance.post(b + "/_posts", d)).data,
		updatePost: async (p, d) => (await axiosInstance.put(b + "/_posts/" + p, d)).data,
		deletePost: async (p) => { await axiosInstance.delete(b + "/_posts/" + p) },
		fileUrl: async (f) => (await axiosInstance.get(b + "/_files/" + f + "/_url")).data,
		students: async () => (await axiosInstance.get(b + "/_students")).data.students,
		roster: async () => { const d = (await axiosInstance.get(b + "/_students")).data; return { minGrade: typeof d.minGrade === "number" ? d.minGrade : 70, students: d.students } },
		pin: async (p, pinned) => (await axiosInstance.post(b + "/_posts/" + p + "/_pin", { pinned })).data,
		units: async () => (await axiosInstance.get(b + "/_units")).data.units,
		createUnit: async (d) => (await axiosInstance.post(b + "/_units", d)).data.units,
		updateUnit: async (id, d) => (await axiosInstance.put(b + "/_units/" + id, d)).data.units,
		deleteUnit: async (id) => (await axiosInstance.delete(b + "/_units/" + id)).data.units,
		settings: async () => (await axiosInstance.get(b + "/_settings")).data,
		saveSettings: async (d) => (await axiosInstance.put(b + "/_settings", d)).data.settings,
		uploadUrl: async (d) => (await axiosInstance.post(b + "/_uploadUrl", d)).data,
	}
}

export const listaDelPersonal = async (todas: boolean, q: string): Promise<{ canSeeAll: boolean; hasMore: boolean; classes: ClaseDelPersonal[] }> =>
	(await axiosInstance.get("/admin/classPortal/_classes", { params: { all: todas ? 1 : 0, q } })).data

export const apiAlumno = (h: number): PortalApiAlumno =>
{
	const b = "/scholar/_class/" + h
	return {
		overview: async () => (await axiosInstanceV1.get(b + "/_overview")).data,
		wall: async (page) => (await axiosInstanceV1.get(b + "/_wall", { params: { page } })).data,
		materials: async (limit) => (await axiosInstanceV1.get(b + "/_materials", { params: limit ? { limit } : {} })).data,
		comments: async (p) => (await axiosInstanceV1.get(b + "/_posts/" + p + "/_comments")).data.comments,
		addComment: async (p, body) => (await axiosInstanceV1.post(b + "/_posts/" + p + "/_comments", { body })).data.comments,
		deleteComment: async (p, c) => (await axiosInstanceV1.delete(b + "/_posts/" + p + "/_comments/" + c)).data.comments,
		createPost: async (d) => (await axiosInstanceV1.post(b + "/_posts", d)).data,
		updatePost: async (p, d) => (await axiosInstanceV1.put(b + "/_posts/" + p, d)).data,
		deletePost: async (p) => { await axiosInstanceV1.delete(b + "/_posts/" + p) },
		fileUrl: async (f) => (await axiosInstanceV1.get(b + "/_files/" + f + "/_url")).data,
		classmates: async () => (await axiosInstanceV1.get(b + "/_classmates")).data,
		checkTodo: async (p, done) => (await axiosInstanceV1.post(b + "/_posts/" + p + "/_check", { done })).data,
		vote: async (p, optionIds) => (await axiosInstanceV1.post(b + "/_posts/" + p + "/_vote", { optionIds })).data,
	}
}

// ---------------------------------------------------------------- Tareas, entregas y Calificar (Parte B, 2026-10-17)

export type FormaDeEntrega = "foto" | "archivo" | "en_clase" | "sin_entrega" | "quiz"
export type EstadoPersonal = "sin_entregar" | "por_calificar" | "calificada" | "devuelta" | "repetir" | "n_a"

export interface ConteoTarea { total: number; entregadas: number; porCalificar: number; calificadas: number; devueltas: number; sinEntregar: number; repetir: number }

export interface TareaDeLista
{
	id: number
	unitId: number | null
	title: string
	status: "publicada" | "programada" | "borrador"
	delivery: FormaDeEntrega
	deliveryLabel: string
	points: number
	category: string | null
	categoryOk: boolean
	dueMs: number | null
	dueLabel: string | null
	overdue: boolean
	publishLabel: string | null
	audience: "clase" | "lista"
	counts: ConteoTarea
	gradable: boolean
	inherited: boolean
	originLabel: string
	/** Parte C: tarea cerrada (ya nadie entrega y lo que falta cuenta 0). */
	closed?: boolean
}

export interface ListaDeTareas
{
	counts: { all: number; porCalificar: number; programadas: number; borradores: number }
	units: { id: number | null; name: string; startsOn: string | null; endsOn: string | null; phase: "proxima" | "en_curso" | "terminada" | null; tasks: TareaDeLista[] }[]
	students: number
}

export interface CriterioRubrica { id?: number; name: string; points: number }

export interface TareaEditable
{
	id: number
	title: string
	body: string | null
	status: "publicada" | "programada" | "borrador"
	delivery: FormaDeEntrega
	points: number
	category: string | null
	unitId: number | null
	dueAt: string | null
	publishAt: string | null
	acceptLate: boolean
	latePenaltyPct: number
	remindDayBefore: boolean
	pinned: boolean
	audience: "clase" | "lista"
	studentIds: number[]
	rubric: CriterioRubrica[]
	files: ArchivoPublicacion[]
	inherited: boolean
	originLabel: string
	hasSubmissions: boolean
	hasGrades: boolean
	rosterCount: number
	/** Parte D: preguntas y ajustes si es quiz. */
	quiz?: QuizCompleto
}

export interface DatosTarea
{
	title?: string
	body?: string
	delivery?: FormaDeEntrega
	points?: number | null
	category?: string | null
	unitId?: number | null
	dueAt?: string | null
	publishAt?: string | null
	draft?: boolean
	pinned?: boolean
	acceptLate?: boolean
	latePenaltyPct?: number
	remindDayBefore?: boolean
	audience?: "clase" | "lista"
	studentIds?: number[]
	rubric?: CriterioRubrica[]
	files?: ArchivoNuevo[]
	/** Parte D: solo con delivery "quiz" (sin ella en un PUT no se tocan preguntas ni ajustes). */
	quiz?: DatosQuiz
}

export interface AlumnoDeEntrega
{
	id: number
	name: string
	initials: string
	photo: string | null
	state: EstadoPersonal
	submittedLabel: string | null
	submittedMs: number
	late: boolean
	files: number
	points: number | null
}

export interface ListaDeEntregas
{
	task: { id: number; title: string; body: string | null; delivery: FormaDeEntrega; deliveryLabel: string; points: number; category: string | null; dueLabel: string | null; overdue: boolean; acceptLate: boolean; latePenaltyPct: number; rubric: { id: number; name: string; points: number }[]; inherited: boolean; closed?: boolean }
	counts: { porCalificar: number; calificadas: number; devueltas: number; sinEntregar: number; repetir: number; total: number }
	students: AlumnoDeEntrega[]
	/** Nota mínima (del servidor); lo que queda abajo va en oro. */
	minGrade?: number
}

export interface ArchivoDeEntrega { id: number; name: string; mime: string | null; size: number; isImage: boolean; url: string | null }

export interface DetalleDeEntrega
{
	student: { id: number; name: string; initials: string; photo: string | null }
	state: EstadoPersonal
	submission: { status: string; submittedLabel: string | null; late: boolean; lateDays: number; comment: string | null; repeatNote: string | null; files: ArchivoDeEntrega[]
		/** While waiting for the redo: the grade it had before (Cancelar Repetición brings it back). */
		previousGrade?: { points: number; comment: string | null; returned: boolean } | null } | null
	grade: { points: number | null; lateDiscount: number; rawPoints: number | null; criteria: Record<string, number> | null; comment: string | null; returned: boolean; gradedBy: string | null } | null
	log: { whenLabel: string; by: string; before: number | null; after: number | null; action: string }[]
}

export interface DatosDeNota { action: "guardar" | "devolver" | "repetir" | "deshacer_repetir"; criteria?: Record<string, number>; points?: number | null; comment?: string }

export interface TarjetasDelMuro
{
	grading: { pending: number; firstTaskId: number | null; tasks: { id: number; title: string; pending: number; delivery?: FormaDeEntrega }[] }
	attention: { id: number; name: string; initials: string; photo: string | null; reasons: string[] }[]
}

export interface ApiTareasPersonal
{
	list(): Promise<ListaDeTareas>
	get(postId: number): Promise<TareaEditable>
	create(d: DatosTarea): Promise<TareaEditable>
	update(postId: number, d: DatosTarea): Promise<TareaEditable>
	submissions(postId: number): Promise<ListaDeEntregas>
	submission(postId: number, studentId: number): Promise<DetalleDeEntrega>
	grade(postId: number, studentId: number, d: DatosDeNota): Promise<DetalleDeEntrega>
	returnGraded(postId: number): Promise<ListaDeEntregas & { returned: number }>
	/** Parte C: cerrar (o reabrir) la tarea. */
	close(postId: number, closed: boolean): Promise<{ closed: boolean }>
	remind(postId: number, studentIds?: number[]): Promise<{ sent: number; skippedRecent: number; pending: number }>
	submissionFileUrl(postId: number, studentId: number, fileId: number): Promise<{ url: string; name: string; mime: string | null }>
}

export const apiTareasPersonal = (h: number): ApiTareasPersonal =>
{
	const b = "/admin/classPortal/" + h + "/_tasks"
	return {
		list: async () => (await axiosInstance.get(b)).data,
		get: async (p) => (await axiosInstance.get(b + "/" + p)).data,
		create: async (d) => (await axiosInstance.post(b, d)).data,
		update: async (p, d) => (await axiosInstance.put(b + "/" + p, d)).data,
		submissions: async (p) => (await axiosInstance.get(b + "/" + p + "/_submissions")).data,
		submission: async (p, s) => (await axiosInstance.get(b + "/" + p + "/_submissions/" + s)).data,
		grade: async (p, s, d) => (await axiosInstance.put(b + "/" + p + "/_submissions/" + s + "/_grade", d)).data,
		returnGraded: async (p) => (await axiosInstance.post(b + "/" + p + "/_return")).data,
		close: async (p, closed) => (await axiosInstance.post(b + "/" + p + "/_close", { closed })).data,
		remind: async (p, ids) => (await axiosInstance.post(b + "/" + p + "/_remind", ids ? { studentIds: ids } : {})).data,
		submissionFileUrl: async (p, s, f) => (await axiosInstance.get(b + "/" + p + "/_submissions/" + s + "/_files/" + f + "/_url")).data,
	}
}

// ---- alumno

export type EstadoAlumno = "pendiente" | "atrasada" | "cerrada" | "entregada" | "repetir" | "devuelta" | "en_clase" | "lectura" | "quiz"

export interface TareaDelAlumno
{
	id: number
	title: string
	unit: string | null
	delivery: FormaDeEntrega
	deliveryLabel: string
	points: number
	dueMs: number | null
	dueLabel: string | null
	state: EstadoAlumno
	late: boolean
	/** Lo que sacó (solo si ya se la devolvieron y el maestro deja ver la nota). */
	score: number | null
	scoreHidden: boolean
	pointsMax: number
}

export interface DetalleTareaAlumno
{
	id: number
	title: string
	body: string | null
	unit: string | null
	delivery: FormaDeEntrega
	deliveryLabel: string
	points: number
	dueMs: number | null
	dueLabel: string | null
	acceptLate: boolean
	latePenaltyPct: number
	rubric: { name: string; points: number }[]
	files: ArchivoPublicacion[]
	state: EstadoAlumno
	canEdit: boolean
	canUndo: boolean
	serverNowMs: number
	submission: { status: string; submittedLabel: string | null; late: boolean; lateDays: number; comment: string | null; repeatNote: string | null; files: ArchivoDeEntrega[] }
	grade: { points: number | null; pointsHidden: boolean; lateDiscount: number; comment: string | null; criteria: { name: string; points: number; max: number }[] | null
		/** Le pidieron repetir, no repitió y se cerró la tarea: cuenta la nota que ya tenía. */
		kept?: boolean } | null
	limits: { maxFiles: number; maxMb: number }
}

export interface ApiTareasAlumno
{
	list(): Promise<{ tasks: TareaDelAlumno[] }>
	get(postId: number): Promise<DetalleTareaAlumno>
	uploadUrl(postId: number, d: { name: string; size: number; mime: string }): Promise<BoletoDeSubida>
	addFiles(postId: number, files: ArchivoNuevo[]): Promise<DetalleTareaAlumno>
	removeFile(postId: number, fileId: number): Promise<DetalleTareaAlumno>
	submit(postId: number, comment: string): Promise<DetalleTareaAlumno>
	undo(postId: number): Promise<DetalleTareaAlumno>
	fileUrl(postId: number, fileId: number): Promise<{ url: string; name: string; mime: string | null }>
}

export const apiTareasAlumno = (h: number): ApiTareasAlumno =>
{
	const b = "/scholar/_class/" + h + "/_tasks"
	return {
		list: async () => (await axiosInstanceV1.get(b)).data,
		get: async (p) => (await axiosInstanceV1.get(b + "/" + p)).data,
		uploadUrl: async (p, d) => (await axiosInstanceV1.post(b + "/" + p + "/_uploadUrl", d)).data,
		addFiles: async (p, files) => (await axiosInstanceV1.post(b + "/" + p + "/_files", { files })).data,
		removeFile: async (p, f) => (await axiosInstanceV1.delete(b + "/" + p + "/_files/" + f)).data,
		submit: async (p, comment) => (await axiosInstanceV1.post(b + "/" + p + "/_submit", { comment })).data,
		undo: async (p) => (await axiosInstanceV1.post(b + "/" + p + "/_undo")).data,
		fileUrl: async (p, f) => (await axiosInstanceV1.get(b + "/" + p + "/_files/" + f + "/_url")).data,
	}
}


// ---------------------------------------------------------------- Libro De Notas y Mis Notas (Parte C, 2026-10-19)

/**
 * Estado de una celda del libro: ok (devuelta, cuenta), sinDevolver (calificada pero el alumno aún no la ve), calificar (entregó y espera
 * nota), pendiente (aún no vence), falta (venció sin entregar; cuenta 0 solo al devolver o cerrar), repetir, cerrada (tarea cerrada sin
 * entrega: cuenta 0) y na (la tarea no es para este alumno).
 */
export type EstadoCelda = "ok" | "sinDevolver" | "calificar" | "pendiente" | "falta" | "repetir" | "cerrada" | "na"

/** score ya trae el descuento por tarde; lateDiscount = puntos descontados (0 si nada). */
export interface CeldaLibro { state: EstadoCelda; score: number | null; lateDiscount?: number }

export interface ColumnaLibro
{
	id: number
	title: string
	category: string | null
	categoryOk: boolean
	points: number
	delivery: FormaDeEntrega
	rubric: boolean
	unit: string | null
	dueMs: number | null
	dueLabel: string | null
	closed: boolean
	fixedPct: number | null
	/** La categoría ya se llenó con pesos fijos y esta tarea no pesa (aviso "sinPeso"). */
	noWeight?: boolean
	toGrade: number
	ungraded: number
	overdue?: boolean
}

export interface CategoriaDeAlumno
{
	name: string
	weight: number
	pct: number | null
	counted: boolean
	/** Lo que pesa de verdad al repartir el peso de las categorías que aún no cuentan. */
	share: number | null
	esAsistencia: boolean
	tasks: { counted: number; total: number }
}

export interface AsistenciaAlumno { present: number; total: number; pct: number | null }

export interface AlumnoDelLibro
{
	id: number
	name: string
	initials: string
	photo: string | null
	final: number | null
	low: boolean
	attendance: AsistenciaAlumno
	categories: CategoriaDeAlumno[]
	cells: CeldaLibro[]
}

export interface LibroDeNotas
{
	scheduleId: number
	scheme: { source: EsquemaDePesos["source"]; sourceLabel: string; categories: { name: string; weight: number; esAsistencia: boolean }[] }
	minGrade: number
	tasks: ColumnaLibro[]
	students: AlumnoDelLibro[]
	summary: {
		average: number | null
		low: number
		withGrade: number
		students: number
		categories: { name: string; weight: number; esAsistencia: boolean; avg: number | null }[]
		unreturned: number
		toGrade: number
	}
	warnings: { type: string; message: string }[]
	serverNowMs?: number
}

export interface ApiNotasPersonal
{
	get(): Promise<LibroDeNotas>
	cell(postId: number, studentId: number, points: number): Promise<LibroDeNotas>
	publish(): Promise<LibroDeNotas & { returned: number }>
	/** El Excel del libro (con la sesión, como blob) y el nombre que manda el servidor. */
	excel(): Promise<{ blob: Blob; name: string | null }>
}

const nombreDeDescarga = (cd: string | undefined): string | null => {
	if (!cd) return null
	const utf = /filename\*=UTF-8''([^;]+)/i.exec(cd)
	if (utf) { try { return decodeURIComponent(utf[1].replace(/"/g, "")) } catch { /* sigue con filename= */ } }
	const simple = /filename="?([^";]+)"?/i.exec(cd)
	return simple ? simple[1] : null
}

export const apiNotasPersonal = (h: number): ApiNotasPersonal =>
{
	const b = "/admin/classPortal/" + h + "/_gradebook"
	return {
		get: async () => (await axiosInstance.get(b)).data,
		cell: async (p, s, points) => (await axiosInstance.put(b + "/_cell/" + p + "/" + s, { points })).data,
		publish: async () => (await axiosInstance.post(b + "/_publish")).data,
		excel: async () => {
			const r = await axiosInstance.get(b + "/_excel", { responseType: "blob" })
			return { blob: r.data as Blob, name: nombreDeDescarga(r.headers?.["content-disposition"]) }
		},
	}
}

// ---- alumno

export interface MiTareaConNota
{
	id: number
	title: string
	unit: string | null
	category: string | null
	points: number
	dueMs: number | null
	dueLabel: string | null
	/** Cómo se entrega (el servidor lo manda desde 0aad27a9). */
	delivery?: FormaDeEntrega
}

export interface MisNotas
{
	title: string
	/** Apagado ("Sus Notas Al Calificar"): ve los comentarios pero ninguna cifra. */
	scoresVisible: boolean
	minGrade: number
	schemeLabel: string
	average: number | null
	low: boolean
	categories: CategoriaDeAlumno[]
	attendance: AsistenciaAlumno
	/** Solo si el maestro lo deja ver y hay al menos 3 alumnos con promedio. */
	groupAverage: number | null
	pending: (MiTareaConNota & { state: EstadoCelda; overdue: boolean; repeat: boolean; closed: boolean })[]
	graded: (MiTareaConNota & {
		score: number | null; scoreHidden: boolean; missing: boolean; comment: string | null; by: string | null; byInitials: string | null
		returnedLabel: string | null; lateDiscount: number
		/** Nota conservada de antes de pedirle repetir (no repitió y la tarea se cerró). */
		kept?: boolean
	})[]
	review: (MiTareaConNota & { submittedLabel: string | null; late: boolean })[]
}

export const misNotasDelAlumno = async (h: number): Promise<MisNotas> => (await axiosInstanceV1.get("/scholar/_class/" + h + "/_grades")).data
